import { useState } from 'react';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxInputGroup,
  ComboboxItem,
  ComboboxList,
} from '@/components/ui/combobox';
import { useWorkspace } from '@/features/workspaces';
import { relationLabel } from '../types';
import {
  TaskRelationViewRelationType,
  type TaskView,
} from '@/lib/api/generated/model';

export interface TaskRelationPickerProps {
  workspaceId: string;
  excludeTaskIds: string[];
  onSelect: (
    task: TaskView,
    relationType: TaskRelationViewRelationType | undefined,
  ) => void;
  onClose: () => void;
}

const RELATION_TYPE_OPTIONS = Object.values(TaskRelationViewRelationType);

/**
 * Choosing a task to connect to, and how.
 *
 * This used to be a modal dialog. It opened on top of the task drawer, and on
 * the task-creation form it opened on top of that form's own dialog, so the one
 * gesture in the feature that needs you to look at what is already there dimmed
 * exactly that. Escape did behave, but focus was dropped on the document body
 * afterwards rather than returned to the control that opened it.
 *
 * Inline now, over the combobox every other picker in this app is built on:
 * labels, assignees, model selection. Its list is its own anchored dropdown, so
 * nothing about search, keyboard handling or focus return is reimplemented here,
 * and there is no layer to stack.
 *
 * Still two steps. Picking a kind stays part of creating the connection rather
 * than something to fix afterwards, because the contract has no update for a
 * relation: changing a kind later means a delete and a create that can fail in
 * between. Asking once, while the person still has the decision in mind, is
 * honest about what the system can do.
 */
export function TaskRelationPicker({
  workspaceId,
  excludeTaskIds,
  onSelect,
  onClose,
}: TaskRelationPickerProps) {
  const workspace = useWorkspace(workspaceId);
  const [query, setQuery] = useState('');
  const [pickedTask, setPickedTask] = useState<TaskView | null>(null);
  const [relationType, setRelationType] =
    useState<TaskRelationViewRelationType>('plain');

  const excluded = new Set(excludeTaskIds);
  const candidates = (workspace.data?.tasks ?? []).filter(
    (task) =>
      !excluded.has(task.id) &&
      // Hidden from the board, so not something to reach for here either
      // (spec 100 FR-013).
      task.hidden_at == null &&
      task.title.toLowerCase().includes(query.trim().toLowerCase()),
  );

  if (pickedTask) {
    return (
      <div className="border-border bg-card flex min-w-0 flex-col gap-2 rounded-md border p-2.5">
        <p className="text-muted-foreground text-xs">
          How is this task related to{' '}
          <span className="text-foreground font-medium">
            {pickedTask.title}
          </span>
          ?
        </p>
        <div className="flex flex-wrap items-center gap-1.5">
          {RELATION_TYPE_OPTIONS.map((option) => (
            <Button
              key={option}
              type="button"
              variant="outline"
              size="sm"
              aria-pressed={relationType === option}
              /*
                The chosen kind wears the app's selected surface, the same tint a
                chosen board card, graph node or phase tab wears. It was a
                `secondary` variant against `outline` ones, which is invisible
                here: `outline` is the background with a border and `secondary`
                is a near-white raised surface, so on a white card all four read
                as one row of identical pills and nothing said which was picked.
              */
              className={cn(
                relationType === option &&
                  'bg-selected border-selected-border text-foreground',
              )}
              onClick={() => setRelationType(option)}
            >
              {relationLabel(option)}
            </Button>
          ))}
        </div>
        <div className="flex justify-end gap-2">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setPickedTask(null)}
          >
            Back
          </Button>
          <Button
            type="button"
            size="sm"
            onClick={() => {
              onSelect(
                pickedTask,
                relationType === 'plain' ? undefined : relationType,
              );
              onClose();
            }}
          >
            Confirm
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-w-0 items-start gap-2">
      <div className="min-w-0 flex-1">
        <Combobox<string>
          items={candidates.map((task) => task.id)}
          itemToStringLabel={(id) =>
            candidates.find((task) => task.id === id)?.title ?? id
          }
          onValueChange={(id) => {
            const picked = candidates.find((task) => task.id === id);
            if (picked) setPickedTask(picked);
          }}
        >
          <ComboboxInputGroup>
            <ComboboxInput
              autoFocus
              aria-label="Search tasks"
              placeholder="Search tasks…"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
            />
          </ComboboxInputGroup>

          <ComboboxContent>
            <ComboboxList>
              {candidates.map((task) => (
                <ComboboxItem key={task.id} value={task.id}>
                  {task.title}
                </ComboboxItem>
              ))}
            </ComboboxList>
            <ComboboxEmpty>No task matches.</ComboboxEmpty>
          </ComboboxContent>
        </Combobox>
      </div>
      <Button type="button" variant="ghost" size="sm" onClick={onClose}>
        Cancel
      </Button>
    </div>
  );
}
